import { useEffect, useState } from 'react';
import api from '../services/api';
import { GAME_TEMPLATES } from '../game/templates';

interface ProjectSummary {
  id: number;
  name: string;
  plays?: number;
  createdAt?: string;
  updatedAt?: string;
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/projects');
        const data = res.data?.data;
        setProjects(Array.isArray(data) ? data : []);
      } catch {
        setError('Could not load projects.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const createProject = async () => {
    const name = newName.trim() || 'Untitled Project';
    try {
      setCreating(true);
      const res = await api.post('/projects', { name });
      const id = res.data?.data?.id;
      if (id != null) {
        window.location.href = `/editor?project=${id}`;
      } else {
        setError('Project created but no id returned.');
        setCreating(false);
      }
    } catch {
      setError('Could not create project.');
      setCreating(false);
    }
  };

  const renameProject = async (p: ProjectSummary) => {
    const next = window.prompt('Rename project', p.name);
    if (next == null) return;
    const name = next.trim();
    if (!name || name === p.name) return;
    try {
      await api.put(`/projects/${p.id}`, { name });
      setProjects((cur) =>
        cur.map((x) => (x.id === p.id ? { ...x, name } : x))
      );
    } catch {
      setError('Could not rename project.');
    }
  };

  const deleteProject = async (p: ProjectSummary) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      setProjects((cur) => cur.filter((x) => x.id !== p.id));
    } catch {
      setError('Could not delete project.');
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };

  return (
    <div
      className="min-h-screen w-full text-white"
      style={{
        background:
          'radial-gradient(1000px 500px at 50% -10%, rgba(43,140,255,0.15), transparent 60%),' +
          '#070710',
      }}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 max-w-6xl mx-auto">
        <a href="/" className="flex items-center gap-3">
          <img src="/yip-logo.png" alt="YIP" className="h-8 w-8" />
          <span className="text-lg font-bold tracking-tight">
            YIP<span className="text-blue-400">.fun</span>
          </span>
        </a>
      </nav>

      <main className="max-w-6xl mx-auto px-6 pt-8 pb-24">
        <h1 className="text-3xl font-extrabold tracking-tight mb-2">Your Projects</h1>
        <p className="text-gray-400 mb-8">Open an existing scene or start a new one.</p>

        {/* Create row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-10">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New project name"
            className="flex-1 bg-gray-900 border border-white/15 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') createProject();
            }}
          />
          <button
            onClick={createProject}
            disabled={creating}
            className="px-6 py-2.5 rounded-lg font-semibold text-white disabled:opacity-50 transition-transform hover:scale-[1.02]"
            style={{ backgroundImage: 'linear-gradient(90deg, #2B8CFF, #7C3AED)' }}
          >
            {creating ? 'Creating…' : 'New Project'}
          </button>
        </div>

        {/* Start from a template */}
        <div className="mb-12">
          <h2 className="text-sm font-semibold text-gray-300 mb-1">
            Start from a template
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            Open a ready-made game, then tweak it and share it.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {GAME_TEMPLATES.map((t) => (
              <a
                key={t.id}
                href={`/editor?template=${t.id}`}
                className="block rounded-2xl border border-purple-500/20 bg-purple-500/[0.06] p-5 hover:border-purple-500/50 hover:bg-purple-500/[0.10] transition-colors"
              >
                <div className="font-semibold mb-1">{t.title}</div>
                <div className="text-xs text-gray-400 leading-relaxed mb-3">
                  {t.description}
                </div>
                <div className="text-sm text-purple-300">Create &amp; play →</div>
              </a>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-gray-400">Loading projects…</p>
        ) : projects.length === 0 ? (
          <div className="border border-white/10 rounded-2xl p-10 text-center text-gray-400">
            No projects yet. Create your first one above.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div
                key={p.id}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/25 hover:bg-white/[0.06] transition-colors"
              >
                <a href={`/editor?project=${p.id}`} className="block">
                  <div className="flex items-center gap-3 mb-3">
                    <img src="/yip-logo.png" alt="" className="h-7 w-7 opacity-80" />
                    <span className="font-semibold truncate">{p.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span>
                      {formatDate(p.updatedAt || p.createdAt)
                        ? `Updated ${formatDate(p.updatedAt || p.createdAt)}`
                        : `Project #${p.id}`}
                    </span>
                    <span className="text-gray-600">·</span>
                    <span className="text-gray-400">
                      ▶ {p.plays ?? 0} {(p.plays ?? 0) === 1 ? 'play' : 'plays'}
                    </span>
                  </div>
                  <div className="mt-4 text-sm text-blue-400">Open →</div>
                </a>
                <div className="mt-3 pt-3 border-t border-white/5 flex gap-4">
                  <button
                    onClick={() => renameProject(p)}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => deleteProject(p)}
                    className="text-xs text-gray-400 hover:text-red-400 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
